import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,

} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ReportCell, CellMapping, OrganizationField, SelectedQuestion, SelectedItemComment } from '../types/reportBuilder.types';
import FilterBuilder from './FilterBuilder';
import AggregationBuilder from './AggregationBuilder';

interface CellMappingStepProps {
  cells: ReportCell[];
  cellMappings: CellMapping[];
  onMappingsChange: (mappings: CellMapping[]) => void;
  selectedFields: OrganizationField[];
  selectedQuestions: SelectedQuestion[];
  selectedItemComments: SelectedItemComment[];
}

const CellMappingStep: React.FC<CellMappingStepProps> = ({
  cells,
  cellMappings,
  onMappingsChange,
  selectedFields,
  selectedQuestions,
  selectedItemComments,
}) => {
  const [expandedCell, setExpandedCell] = useState<string | false>(false);

  const getMappingForCell = (cellId: string): CellMapping | undefined => {
    return cellMappings.find(m => m.cellId === cellId);
  };

  const updateMapping = (cellId: string, updates: Partial<CellMapping>) => {
    const existing = getMappingForCell(cellId);
    if (existing) {
      onMappingsChange(
        cellMappings.map(m => (m.cellId === cellId ? { ...m, ...updates } : m))
      );
    } else {
      const newMapping: CellMapping = {
        cellId,
        sourceType: updates.sourceType || 'default',
        ...updates,
      };
      onMappingsChange([...cellMappings, newMapping]);
    }
  };

  const handleSourceTypeChange = (cellId: string, sourceType: CellMapping['sourceType']) => {

    const baseUpdate: Partial<CellMapping> = { sourceType };

    // Reset fields when changing source type
    if (sourceType === 'field') {
      baseUpdate.sourceId = selectedFields[0] || undefined;
      baseUpdate.aggregation = undefined;
      baseUpdate.filters = undefined;
      baseUpdate.positionSelector = undefined;
    } else if (sourceType === 'question') {
      baseUpdate.sourceId = selectedQuestions[0]
        ? `${selectedQuestions[0].programVersionItemId}:${selectedQuestions[0].question.uuid}`
        : undefined;
    } else if (sourceType === 'itemComment') {
      baseUpdate.sourceId = selectedItemComments[0]
        ? selectedItemComments[0].programVersionItemId
        : undefined;
      baseUpdate.aggregation = undefined;
      baseUpdate.filters = undefined;
      baseUpdate.positionSelector = undefined;
    } else if (sourceType === 'constant') {
      baseUpdate.constantValue = '';
      baseUpdate.sourceId = undefined;
    } else if (sourceType === 'default') {
      baseUpdate.defaultValue = '';
      baseUpdate.sourceId = undefined;
    }

    updateMapping(cellId, baseUpdate);
  };

  const renderMappingEditor = (cell: ReportCell) => {
    const mapping = getMappingForCell(cell.id);

    return (
      <Box>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Tipo de Origen</InputLabel>
          <Select
            value={mapping?.sourceType || 'default'}
            onChange={(e) => handleSourceTypeChange(cell.id, e.target.value as CellMapping['sourceType'])}
            label="Tipo de Origen"
          >
            <MenuItem value="field">Campo de Organización/Productor</MenuItem>
            <MenuItem value="question">Pregunta del Formulario</MenuItem>
            {selectedItemComments.length > 0 && (
              <MenuItem value="itemComment">Comentario del Item</MenuItem>
            )}
            <MenuItem value="constant">Valor Constante</MenuItem>
            <MenuItem value="default">Valor por Defecto</MenuItem>
          </Select>
        </FormControl>

        {mapping?.sourceType === 'field' && (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Campo</InputLabel>
              <Select
                value={mapping.sourceId || ''}
                onChange={(e) => updateMapping(cell.id, { sourceId: e.target.value })}
                label="Campo"
              >
                {selectedFields.map((field) => (
                  <MenuItem key={field} value={field}>
                    {field}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        {mapping?.sourceType === 'question' && (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Pregunta</InputLabel>
              <Select
                value={mapping.sourceId || ''}
                onChange={(e) => {
                  const selectedKey = e.target.value;
                  const selectedQuestion = selectedQuestions.find(
                    q => `${q.programVersionItemId}:${q.question.uuid}` === selectedKey
                  );
                  updateMapping(cell.id, {
                    sourceId: selectedKey,
                    questionType: selectedQuestion?.question.type
                  });
                }}
                label="Pregunta"
              >
                {selectedQuestions.map((q) => {
                  const key = `${q.programVersionItemId}:${q.question.uuid}`;
                  return (
                    <MenuItem key={key} value={key}>
                      {q.question.text} ({q.programVersionItem.cpNumber})
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {mapping.sourceId && (() => {
              const question = selectedQuestions.find(
                q => `${q.programVersionItemId}:${q.question.uuid}` === mapping.sourceId
              );
              return (
                <>
                  <AggregationBuilder
                    aggregation={mapping.aggregation}
                    positionSelector={mapping.positionSelector}
                    separator={mapping.separator}
                    onAggregationChange={(agg) => updateMapping(cell.id, { aggregation: agg })}
                    onPositionSelectorChange={(selector) =>
                      updateMapping(cell.id, { positionSelector: selector })
                    }
                    onSeparatorChange={(sep) => updateMapping(cell.id, { separator: sep })}
                    questionType={question?.question.type}
                    question={question?.question}
                  />

                  <FilterBuilder
                    filters={mapping.filters || []}
                    onFiltersChange={(filters) => updateMapping(cell.id, { filters })}
                    availableFields={selectedFields}
                    availableQuestions={selectedQuestions}
                  />
                </>
              );
            })()}
          </>
        )}

        {mapping?.sourceType === 'itemComment' && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Comentario del Item</InputLabel>
            <Select
              value={mapping.sourceId || ''}
              onChange={(e) => updateMapping(cell.id, { sourceId: e.target.value })}
              label="Comentario del Item"
            >
              {selectedItemComments.map((ic) => (
                <MenuItem key={ic.programVersionItemId} value={ic.programVersionItemId}>
                  {ic.programVersionItem.cpNumber}: {ic.programVersionItem.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {mapping?.sourceType === 'constant' && (
          <TextField
            fullWidth
            label="Valor Constante"
            value={mapping.constantValue || ''}
            onChange={(e) => updateMapping(cell.id, { constantValue: e.target.value })}
            sx={{ mb: 2 }}
          />
        )}

        {mapping?.sourceType === 'default' && (
          <TextField
            fullWidth
            label="Valor por Defecto"
            value={mapping.defaultValue || ''}
            onChange={(e) => updateMapping(cell.id, { defaultValue: e.target.value })}
            sx={{ mb: 2 }}
          />
        )}

        {(mapping?.sourceType === 'field' || mapping?.sourceType === 'question') && (
          <TextField
            fullWidth
            label="Valor por Defecto (si está vacío)"
            value={mapping.defaultValue || ''}
            onChange={(e) => updateMapping(cell.id, { defaultValue: e.target.value })}
            sx={{ mb: 2 }}
            helperText="Este valor se usará si el campo/pregunta está vacío"
          />
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Mapea los valores de las celdas
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Para cada celda, define de dónde provendrá su valor. Puedes usar campos, preguntas con agregaciones y filtros, valores constantes o valores por defecto.
      </Typography>

      {cells.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No hay celdas definidas. Regresa al paso anterior para crear celdas.
          </Typography>
        </Paper>
      ) : (
        cells
          .sort((a, b) => a.order - b.order)
          .map((cell) => {
            const mapping = getMappingForCell(cell.id);
            const isMapped = mapping && mapping.sourceType !== 'default';

            return (
              <Accordion
                key={cell.id}
                expanded={expandedCell === cell.id}
                onChange={() => setExpandedCell(expandedCell === cell.id ? false : cell.id)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Typography variant="body1" sx={{ flexGrow: 1 }}>
                      {cell.name}
                    </Typography>
                    {isMapped ? (
                      <Typography variant="caption" color="success.main" sx={{ mr: 2 }}>
                        ✓ Mapeado
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="warning.main" sx={{ mr: 2 }}>
                        ⚠️ Sin mapear
                      </Typography>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {renderMappingEditor(cell)}
                </AccordionDetails>
              </Accordion>
            );
          })
      )}
    </Box>
  );
};

export default CellMappingStep;
